/**
 * Timezone utility functions for consistent timestamp formatting
 */

const DISPLAY_STORAGE_KEY = 'dac_display_preferences';
const DEFAULT_TIMEZONE = 'Asia/Taipei';

/**
 * Get the user's preferred timezone from localStorage
 * @returns Timezone string (defaults to Asia/Taipei)
 */
export function getUserTimezone(): string {
  try {
    const saved = localStorage.getItem(DISPLAY_STORAGE_KEY);
    if (saved) {
      const preferences = JSON.parse(saved);
      return preferences.timezone || DEFAULT_TIMEZONE;
    }
  } catch (err) {
    console.error('Failed to load timezone preference', err);
  }
  return DEFAULT_TIMEZONE;
}

/**
 * Format a date string with the user's preferred timezone
 * @param dateString - ISO date string or Date object
 * @param options - Additional Intl.DateTimeFormat options (timezone will be overridden)
 * @returns Formatted date string in the user's timezone
 */
export function formatWithUserTimezone(
  dateString: string | Date,
  options: Intl.DateTimeFormatOptions = {}
): string {
  const timezone = getUserTimezone();
  const date = typeof dateString === 'string' ? new Date(dateString) : dateString;

  const defaultOptions: Intl.DateTimeFormatOptions = {
    timeZone: timezone,
    timeZoneName: 'short',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: true,
    ...options,
  };

  return date.toLocaleString('en-US', defaultOptions);
}

/**
 * Format a cache timestamp (short format)
 * Example: "10/28, 02:09:48 PM GMT+8"
 * @param dateString - ISO date string or Date object
 * @returns Formatted timestamp string
 */
export function formatCacheTimestamp(dateString: string | Date): string {
  return formatWithUserTimezone(dateString, {
    year: undefined, // Exclude year from cache timestamp
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: true,
    timeZoneName: 'short',
  });
}

/**
 * Format a data source timestamp (full format)
 * Example: "10/28/2025, 02:09:48 PM GMT+8"
 * @param dateString - ISO date string or Date object
 * @returns Formatted timestamp string
 */
export function formatDataTimestamp(dateString: string | Date): string {
  return formatWithUserTimezone(dateString, {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: true,
    timeZoneName: 'short',
  });
}

/**
 * Format a date for article display (date only)
 * Example: "Oct 28, 2025"
 * @param dateString - ISO date string or Date object
 * @returns Formatted date string
 */
export function formatArticleDate(dateString: string | Date): string {
  const timezone = getUserTimezone();
  const date = typeof dateString === 'string' ? new Date(dateString) : dateString;

  return date.toLocaleDateString('en-US', {
    timeZone: timezone,
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}
